import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { vouchersApi, type Voucher, type VoucherReference } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText } from "../ui/useLoad";
import { ErrorNote, PageHead } from "../ui/bits";
import { OrderLink } from "../orders/Orders";
import { DOCS, docTitle } from "./docs";
import { Worked } from "./Worked";
import { fmtDate } from "../companies/CompanyPicker";
import { EInvoicePanel } from "../gst/EInvoicePanel";
import { EWayPanel } from "../gst/EWayPanel";
import { AmendPanel } from "../gst/Gstr1Extras";
import { Printer } from "lucide-react";
import { DocMessages } from "../messages/MessagePages";
import { Attachments } from "../files/Attachments";
import { paymentsApi } from "../lib/api";
import { fmtAmount } from "../lib/money";
import { useLoad } from "../ui/useLoad";
import { toUrl } from "../shell/routes";
import { TERMS_LABEL } from "./entry";

type Act = "reverse" | "void" | "delete" | "note" | null;

// A posted (or voided / deleted) voucher: read-only, with reverse, void, delete (each with a reason), cancel with a note
// (a GST invoice) and copy to a new entry.
export function VoucherView({ v, ref_: ref, onChange }: { v: Voucher; ref_: VoucherReference; onChange: (v: Voucher) => void }) {
  const { company } = useCompany();
  const nav = useNavigate();
  const sendNow = !!(useLocation().state as { send?: boolean } | null)?.send;
  const [act, setAct] = useState<Act>(null);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(ref.today > v.date ? ref.today : v.date);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canPost = ref.can.post;
  const noteName = v.type.base_type === "purchase" ? "debit note" : "credit note";

  const run = async () => {
    if (!reason.trim()) { setErr("Give a reason; it is kept with the entry."); return; }
    setBusy(true); setErr(null);
    try {
      if (act === "reverse") { const r = await vouchersApi.reverse(company.id, v.id, date, reason); nav(toUrl(`vouchers/v/${r.id}`)); }
      else if (act === "note") { const r = await vouchersApi.cancelWithNote(company.id, v.id, date, reason, canPost); nav(toUrl(`vouchers/v/${r.id}`)); }
      else if (act === "void") onChange(await vouchersApi.void(company.id, v.id, reason));
      else if (act === "delete") onChange(await vouchersApi.remove(company.id, v.id, reason));
      setAct(null); setReason("");
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };

  const status = v.status === "posted" ? null : <span className={`tag big ${v.status}`}>{v.status}</span>;
  return (
    <section className="page voucher"
      onKeyDown={(e) => { if (e.key === "Escape" && act) { e.preventDefault(); e.stopPropagation(); setAct(null); } }}>
      <PageHead crumb={DOCS[v.type.base_type]?.section ?? "Transactions"} title={`${v.document && v.document.kind !== "credit_note" ? v.document.title : docTitle(v.type)} ${v.number ?? ""}`}>{status}
        {(v.type.base_type === "sales" || v.type.base_type === "credit_note") && v.status !== "draft" &&
          <Link className="button" to={toUrl(`vouchers/v/${v.id}/print`)}><Printer size={15} aria-hidden="true" /> Print</Link>}
        {["sales", "credit_note", "receipt"].includes(v.type.base_type) && v.status === "posted" && <DocMessages companyId={company.id} target={{ voucher_id: v.id }} autoOpen={sendNow} />}</PageHead>
      {v.type.base_type === "sales" && v.status === "posted" && <OnlinePayments companyId={company.id} voucherId={v.id} />}
      {(v.type.base_type === "sales" || v.type.base_type === "credit_note") && v.status !== "draft" && <AmendPanel voucherId={v.id} />}
      <Attachments companyId={company.id} kind="voucher" id={v.id} canEdit={v.status !== "deleted"} />
      <p className="muted">{fmtDate(v.date)}{v.reference_no ? ` · Ref. ${v.reference_no}` : ""}
        {v.entry.order_id && <> · <OrderLink id={v.entry.order_id} kind={v.type.base_type === "purchase" ? "purchase" : "sales"} /></>}
        {v.reversal_of && <> · reverses <Link to={toUrl(`vouchers/v/${v.reversal_of.id}`)}>{v.reversal_of.number}</Link></>}
        {v.reversed_by && <> · reversed by <Link to={toUrl(`vouchers/v/${v.reversed_by.id}`)}>{v.reversed_by.number}</Link></>}
        {v.currency && v.base_currency && v.currency !== v.base_currency && <> · in {v.currency} at {Number(v.fx_rate).toLocaleString("en-IN", { maximumFractionDigits: 6 })}</>}
        {(v.fx_journals ?? []).map((j) => <span key={j.id}> · exchange difference in <Link to={toUrl(`vouchers/v/${j.id}`)}>{j.number}</Link>{j.status !== "posted" ? ` (${j.status})` : ""}</span>)}
        {v.fx_source && <> · exchange difference on <Link to={toUrl(`vouchers/v/${v.fx_source.id}`)}>{v.fx_source.number}</Link></>}</p>
      {v.document && v.document.declarations.length > 0 && <p className="declarations small">{v.document.declarations.join(" · ")}</p>}
      {v.document && v.status === "posted" && v.document.warnings.map((w) => <p key={w} className="notice small">{w}</p>)}
      {v.status_reason && <p className="notice">{v.status === "voided" ? "Voided" : "Deleted"}: {v.status_reason}</p>}
      <div className="voucher-grid">
        <div className="voucher-main">
          <Worked v={v} title="Entries" />
          {v.narration && <p><span className="muted small">Narration</span><br />{v.narration}</p>}
          <DocDetails e={v.entry} />
        </div>
        <aside className="voucher-side">
          {v.gst && v.status === "posted" && <EInvoicePanel v={v} />}
          {v.gst && v.status === "posted" && v.type.base_type === "sales" && <EWayPanel v={v} states={(ref.gst?.states ?? []).map((x) => ({ value: x.code, label: `${x.code} ${x.name}` }))} />}
          <div className="card panel actions-panel">
            {act ? (
              <>
                <h3>{act === "note" ? `Cancel with a ${noteName}` : `${act === "reverse" ? "Reverse" : act === "void" ? "Void" : "Delete"} this voucher`}</h3>
                <p className="muted small">{act === "note" ? `Makes a ${noteName} for the whole invoice: the same lines${v.lines.some((l) => l.item) ? " and items (stock comes back)" : ""} and GST, linked to it for the returns and set against its bill. ${canPost ? "It is posted at once" : "It is saved as a draft for someone who can post"}; the reason goes on its narration.`
                  : act === "reverse" ? "Posts the opposite entry on the date you choose; this entry stays as it is."
                  : act === "void" ? "Keeps the number; the entry drops out of every balance."
                  : "Takes it out of the books and frees its number; it stays in the edit log."}</p>
                {(act === "reverse" || act === "note") && <label>Date<input type="date" value={date} min={v.date} onChange={(e) => setDate(e.target.value)} /></label>}
                <label>Reason<input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void run(); } }} /></label>
                <div className="row"><button className="danger" onClick={run} disabled={busy}>{busy ? "Working…" : "Confirm"}</button>
                  <button className="link" onClick={() => setAct(null)}>Cancel <kbd>Esc</kbd></button></div>
              </>
            ) : (
              <>
                {v.can.reverse && !v.reversed_by && <button onClick={() => setAct("reverse")}>Reverse…</button>}
                {v.can.cancel_note && <button onClick={() => setAct("note")}>Cancel with a {noteName}…</button>}
                {v.reverse_note && v.status === "posted" && !v.can.cancel_note && <p className="muted small">Not reversed: {v.reverse_note}.</p>}
                {v.can.void && <button onClick={() => setAct("void")}>Void…</button>}
                {v.can.delete && (v.delete_blocker
                  ? <p className="muted small">Can't be deleted: {v.delete_blocker}.</p>
                  : <button className="danger-link link" onClick={() => setAct("delete")}>Delete…</button>)}
                {ref.can.draft && !v.entry.as_journal && <button onClick={() => nav(toUrl(`vouchers/new/${v.type.base_type}`), { state: { copy: v.entry } })}>Copy to a new entry</button>}
                {ref.can.draft && !v.entry.as_journal && (v.type.base_type === "sales" || v.type.base_type === "purchase") &&
                  <button onClick={() => nav(toUrl(`recurring/${v.type.base_type}/new`), { state: { copy: v.entry } })} title="Make this every month, quarter or year">Repeat</button>}
                <button className="link" onClick={() => nav(toUrl(`vouchers/daybook`))}>Transaction register <kbd>Esc</kbd></button>
              </>
            )}
            <ErrorNote text={err} />
          </div>
        </aside>
      </div>
    </section>
  );
}

// payments made through the invoice's "Pay online" link (sql/230)
function OnlinePayments({ companyId, voucherId }: { companyId: string; voucherId: string }) {
  const { data } = useLoad(() => paymentsApi.links(companyId, voucherId), [companyId, voucherId]);
  const paid = (data ?? []).filter((k) => k.status === "paid" || k.problem);
  if (!paid.length) return null;
  return <p className="notice small">{paid.map((k) => k.status === "paid"
    ? <span key={k.id}>Paid online {fmtAmount(k.amount)} ({k.provider}{k.mode === "test" ? ", test" : ""}{k.fee ? `, fee ${fmtAmount(k.fee)}` : ""}) · receipt {k.receipt_id ? <Link to={toUrl(`vouchers/v/${k.receipt_id}`)}>{k.receipt}</Link> : ""}. </span>
    : <span key={k.id}>{k.problem}. </span>)}</p>;
}

// what the form added to an invoice or bill: subject, terms, due date, salesperson, notes, terms and conditions
function DocDetails({ e }: { e: Voucher["entry"] }) {
  const rows: [string, string][] = [];
  if (e.subject) rows.push(["Subject", e.subject]);
  if (e.order_number) rows.push(["Order number", e.order_number]);
  if (e.terms) rows.push(["Terms", TERMS_LABEL[e.terms] ?? e.terms]);
  if (e.due_date) rows.push(["Due date", fmtDate(e.due_date)]);
  if (e.notes) rows.push(["Notes", e.notes]);
  if (e.terms_text) rows.push(["Terms and conditions", e.terms_text]);
  if (!rows.length) return null;
  return <dl className="doc-details">{rows.map(([k, x]) => <div key={k}><dt className="muted small">{k}</dt><dd>{x}</dd></div>)}</dl>;
}
